import React, { useState, useEffect } from 'react';
import Mediapicker from './Mediapicker';
import MultiUploader from './Seller/MultiUploader';

/**
 * Form component that renders controlled form fields based on a fields configuration
 *
 * @param {Object} props
 * @param {Array} props.fields - Array of field configuration objects
 * @param {Object} props.initialValues - Initial values for form fields
 * @param {Object} props.errors - Validation errors object
 * @param {Function} props.onSubmit - Form submission handler
 * @param {Function} props.onChange - Field change handler called with values, field name, and value
 * @param {string} props.className - CSS class for form wrapper
 * @param {React.ReactNode} props.children - Additional content to render after fields
 */
function Form({
    fields = [],
    values = {},
    errors = {},
    onSubmit,
    onChange,
    className = '',
    children,
    debounce = 0, // debounce delay in ms, 0 = no debounce
    as: Element = 'form'
}) {
    const [_values, setValues] = useState(values);
    const debounceTimeout = React.useRef();
    const prevValuesRef = React.useRef(values);

    useEffect(() => {
        // Only update internal state if props.values actually changed
        if (JSON.stringify(prevValuesRef.current) !== JSON.stringify(values)) {
            setValues(values);
            prevValuesRef.current = values;
        }
    }, [values]);

    useEffect(() => {
        // Cleanup on unmount
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    const handleFieldChange = (name, value) => {
        const newValues = { ..._values, [name]: value };
        setValues(newValues);
        // Only call onChange if the value actually changed
        if (onChange && _values[name] !== value) {
            if (debounce > 0) {
                if (debounceTimeout.current) {
                    clearTimeout(debounceTimeout.current);
                }
                debounceTimeout.current = setTimeout(() => {
                    onChange(newValues, name, value);
                }, debounce);
            } else {
                onChange(newValues, name, value);
            }
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (onSubmit) {
            onSubmit(_values);
        }
    };

    const renderField = (field) => {
        const {
            name,
            type = 'text',
            label,
            placeholder,
            required = false,
            options = [],
            help,
            accept,
            rows = 4,
            render,
            labelAddon,
            className: fieldClassName = '',
            groupStyle = {},
            ...fieldProps
        } = field;

        const value = _values[name];
        const error = errors[name];
        const fieldId = `field-${name}`;

        const commonProps = {
            id: fieldId,
            name,
            required,
            className: fieldClassName,
            'aria-describedby': help ? `${fieldId}-help` : undefined,
            'aria-invalid': error ? 'true' : undefined,
            'aria-errormessage': error ? `${fieldId}-error` : undefined,
            ...fieldProps
        };

        let inputElement;

        switch (type) {
            case 'render':
                if (typeof render !== 'function') {
                    console.warn(`Render field "${name}" is missing a render function`);
                    return null;
                }
                inputElement = render({
                    name,
                    value,
                    values: _values,
                    error,
                    fieldId,
                    onChange: handleFieldChange,
                });
                break;

            case 'text':
            case 'email':
            case 'password':
            case 'number':
            case 'url':
            case 'tel':
            case 'date':
                inputElement = (
                    <input
                        {...commonProps}
                        type={type}
                        value={value}
                        placeholder={placeholder}
                        onChange={(e) => handleFieldChange(name, e.target.value)}
                        step={type === 'number' ? fieldProps.step || 'any' : undefined}
                        min={type === 'number' ? fieldProps.min : undefined}
                        max={type === 'number' ? fieldProps.max : undefined}
                    />
                );
                break;

            case 'textarea':
                inputElement = (
                    <textarea
                        {...commonProps}
                        value={value}
                        placeholder={placeholder}
                        rows={rows}
                        onChange={(e) => handleFieldChange(name, e.target.value)}
                    />
                );
                break;

            case 'select':
                inputElement = (
                    <select
                        {...commonProps}
                        value={value}
                        onChange={(e) => handleFieldChange(name, e.target.value)}
                    >
                        {placeholder && (
                            <option value="" disabled>
                                {placeholder}
                            </option>
                        )}
                        {options.map((option) => (
                            <option
                                key={option.value}
                                value={option.value}
                            >
                                {option.label}
                            </option>
                        ))}
                    </select>
                );
                break;

            case 'checkbox':
                inputElement = (
                        <input
                            {...commonProps}
                            type="checkbox"
                            checked={Boolean(value)}
                            onChange={(e) => handleFieldChange(name, e.target.checked)}
                        />
                );
                break;

            case 'radio':
                inputElement = (
                    <div className="radio-group">
                        {options.map((option) => (
                            <label key={option.value} className="radio-option">
                                <input
                                    type="radio"
                                    name={name}
                                    value={option.value}
                                    checked={value === option.value}
                                    onChange={(e) => handleFieldChange(name, e.target.value)}
                                    required={required}
                                />
                                <span>{option.label}</span>
                            </label>
                        ))}
                    </div>
                );
                break;

            case 'mediapicker':
            case 'file':
                inputElement = (
                    <Mediapicker
                        name={name}
                        initialValue={value}
                        accept={accept}
                        required={required}
                        onChange={(selectedValue) => handleFieldChange(name, selectedValue)}
                        {...fieldProps}
                    />
                );
                break;

            case 'submit':
                return (
                    <div key={name} className={`group ${fieldClassName}`}>
                        <input
                            type="submit"
                            value={label || 'Submit'}
                            className="button"
                            {...fieldProps}
                        />
                    </div>
                );

            case 'hidden':
                return (
                    <input
                        key={name}
                        type="hidden"
                        name={name}
                        value={value}
                    />
                );

            case 'multiuploader':
                inputElement = (
                    <MultiUploader
                        name={name}
                        accept={accept}
                        initialValue={value}
                        required={required}
                        onChange={(fileIds) => handleFieldChange(name, fileIds)}
                        {...fieldProps}
                    />
                );
                break;

            default:
                console.warn(`Unknown field type: ${type}`);
                return null;
        }

        return (
            <div key={name} className={`group ${error ? 'error' : ''}`} style={groupStyle}>
                {label && type !== 'checkbox' && labelAddon && (
                    <div className="translatable-label">
                        <label htmlFor={fieldId}>
                            {label}
                            {required && <span className="required" aria-label="required">*</span>}
                        </label>
                        {labelAddon}
                    </div>
                )}

                {label && type !== 'checkbox' && !labelAddon && (
                    <label htmlFor={fieldId}>
                        {label}
                        {required && <span className="required" aria-label="required">*</span>}
                    </label>
                )}

                {label && type === 'checkbox' && placeholder && (
                    <label htmlFor={fieldId} className="checkbox-label">
                        {label}
                        {required && <span className="required" aria-label="required">*</span>}
                    </label>
                )}

                {type === 'checkbox' && (
                    <label htmlFor={fieldId} className="checkbox">
                        {inputElement}
                        <span>{placeholder || label}</span>
                        {required && <span className="required" aria-label="required">*</span>}
                    </label>
                )}

                {type !== 'checkbox' && inputElement}

                {help && <p className="help" id={`${fieldId}-help`}>{help}</p>}

                {error && <p className="error-message" id={`${fieldId}-error`} role="alert">{error}</p>}
            </div>
        );
    };

    return (
        <Element onSubmit={Element === 'form' ? handleSubmit : undefined} className={className}>
            {fields.map(renderField)}
            {children}
        </Element>
    );
}

export default Form;
